import { Component, ViewChild, TemplateRef, OnChanges, Input, ChangeDetectorRef, SimpleChanges, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { MonitorColumn, MonitorLayout, PresentationMonitorGroup, PresentationMonitorItem, PresentationMonitorItemStatus } from '../../models/monitor-status';
import { GenericModalService, ModalSettingsTemplate } from '../../../shared/generic-modal/generic-modal.service';
import { MonitorService } from '../../services/monitor.service';
import { DeveloperArtifactService } from '../../../developer/services/developer-artifact.service';
import { EmbeddedItemsService } from '../../services/embedded-items.service';
import { LoadingPanelService } from '../../../shared/loading-panel/loading-panel.service';
import { MonitorItemDialogComponent } from '../monitor-item-dialog/monitor-item-dialog.component';
import { CdkDragDrop, CdkDropList, CdkDrag, moveItemInArray, transferArrayItem, CdkDragStart, CdkDropListGroup } from '@angular/cdk/drag-drop';
import { DateTimeService } from '../../../shared/datetime/datetime.service';
import { UserSetting } from '../../../user/models/user-setting';
import { UserSettingsService } from '../../../user/services/user-settings.service';
import { NgStyle, NgClass } from '@angular/common';
import { StatusTrafficLightComponent } from '../../../shared/status-traffic-light/status-traffic-light.component';

@Component({
    selector: "app-monitor-display",
    templateUrl: './monitor-display.component.html',
    styleUrls: ['./monitor-display.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CdkDropListGroup, CdkDropList, NgStyle, CdkDrag, NgClass, StatusTrafficLightComponent]
})
export class MonitorDisplayComponent implements OnChanges, OnInit, OnDestroy {
    
    @Input()
    public monitorLayout: MonitorLayout;

    @Input()
    public isDragging: boolean;

    @Output()
    public isDraggingChange = new EventEmitter<boolean>();

    @ViewChild('viewMonitorItemDetails', { static: true }) viewMonitorItemTemplate: TemplateRef<any>;
    @ViewChild('grid', { static: true }) monitorDisplayGrid;  
    @ViewChild('audioPlayerDing', { static: true }) audioPlayerDing;
    @ViewChild('audioPlayerDong', { static: true }) audioPlayerDong;

    public tileWidthRatio: number;
    public tileWidth: number;   
    public showTypes = new Map<number, number>();
    public minDate: string = '0001-01-01T00:00:00+01:00';
    private userSettingKey = 'Monitor_Status_User_Layout';

    @Output() triggerRefresh = new EventEmitter<boolean>();

    constructor(
        private dateTimeService: DateTimeService,
        private userSettingsService: UserSettingsService,
        private monitorService: MonitorService, 
        private modalService: GenericModalService, 
        private developerArtifactService: DeveloperArtifactService,
        private embeddedItemsService: EmbeddedItemsService,
        private loadingPanelService: LoadingPanelService,
        private cdRef: ChangeDetectorRef) {
    }

    ngOnInit() {

    }

    ngOnDestroy() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.monitorLayout && this.monitorLayout) {
            
            this.handleEmptyColumns();
            this.calculateTileWidth();
        }
    }

    calculateTileWidth() {
        let longest = 1;
        for (let monitorColumn of this.monitorLayout.MonitorColumns) {
            for (let monitorGroup of monitorColumn.MonitorGroups) {
                let groupLongest = monitorGroup.MonitorItems.reduce((a, b) => a.Name.length > b.Name.length ? a : b)
                    .Name.length;
                if (groupLongest > longest)
                    longest = groupLongest;
            };
        };

        //Set ratio to at least 8
        this.tileWidth = Math.floor(longest * 8) + 95;
    }
    
    async clickMonitorItem(monitorItem: PresentationMonitorItem) {
        
        let monitorItemStatuses: PresentationMonitorItemStatus[] = [];

        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            monitorItemStatuses = await this.monitorService.getPresentationMonitorItemStatuses(monitorItem.Id);
            
            if (monitorItem.ArtifactId == null) {
                monitorItem.ArtifactName = this.embeddedItemsService.getDisplayNameForEmbeddedItem(monitorItem.EmbeddedTypeArea);
            } else {
                let assemblyArtifact = await this.developerArtifactService.getAssemblyArtifact(monitorItem.ArtifactId);
                monitorItem.ArtifactName = assemblyArtifact.Name;
            }
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }

        const componentData = {
            selectedMonitorItem: monitorItem,
            selectedMonitorItemStatuses: monitorItemStatuses,
        };
        
        try {
            let doRefresh = await this.modalService.openComponent(MonitorItemDialogComponent, componentData, new ModalSettingsTemplate('lg', 'static'));
            if (doRefresh !== undefined && doRefresh === true) {
                this.triggerRefresh.emit(true);
            }
        } catch (err) {

        }
    }

    clickMonitorGroupHeader(monitorGroup: PresentationMonitorGroup) {        
        if (monitorGroup.ShowType != 1) {
            this.showTypes.set(monitorGroup.Id, ShowType.ShowAll);
            monitorGroup.ShowType = ShowType.ShowAll;
        }
        else {
            this.showTypes.set(monitorGroup.Id, ShowType.AlwaysCollapsed);
            monitorGroup.ShowType = ShowType.AlwaysCollapsed;
        }   
        this.saveLayoutAsUserSetting();
    }

    dragStart(event: CdkDragStart<PresentationMonitorGroup>) {
        this.isDragging = true;
        this.isDraggingChange.emit(this.isDragging);
    }

    drop(event: CdkDragDrop<PresentationMonitorGroup[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        }
        this.handleEmptyColumns();
        this.handleColumnAndGroupIndexes();
        this.saveLayoutAsUserSetting()
        this.isDragging = false;
        this.isDraggingChange.emit(this.isDragging);
    }

    handleEmptyColumns() {
        this.monitorLayout.MonitorColumns = this.monitorLayout.MonitorColumns.filter(x => x.MonitorGroups.length > 0);
        let emptyRightColumn = {Id: this.monitorLayout.MonitorColumns.length, MonitorGroups: []} as MonitorColumn;
        this.monitorLayout.MonitorColumns.push(emptyRightColumn);
    }

    handleColumnAndGroupIndexes() {
        for (let i = 0; i < this.monitorLayout.MonitorColumns.length; i++) {
            const column = this.monitorLayout.MonitorColumns[i];
            column.Id = i;
            for (let j = 0; j < column.MonitorGroups.length; j++) {
                column.MonitorGroups[j].SortOrder = j;
            }
        }
    }

    hasPausedChild(monitorGroup: PresentationMonitorGroup): boolean {
        let paused = false;
        monitorGroup.MonitorItems.forEach(element => {
            if (element.AbsenceItemTemplate !== null && paused === false) {
                paused = true;
            }
        });
        return paused;
    }

    saveLayoutAsUserSetting() {
        let clonedLayout = JSON.parse(JSON.stringify(this.monitorLayout));
        clonedLayout.MonitorColumns = clonedLayout.MonitorColumns.filter(x => x.MonitorGroups.length > 0);
        clonedLayout.MonitorColumns.forEach(function(column) {
            column.MonitorGroups.forEach(function(group) {
                group.MonitorItems = [];
            })
        });

        let userLayoutJsonString = JSON.stringify(clonedLayout);

        const userSetting = new UserSetting(this.userSettingKey, undefined, userLayoutJsonString);
        return this.userSettingsService.saveSingleSettings(userSetting).then(() => { });
    }
}

export class ShowType {    
    public static readonly ShowAll = 1;
    public static readonly ShowWarnings = 2;
    public static readonly ShowErrors = 3;
    public static readonly AlwaysCollapsed = 4;
}
